"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { User, CreditCard, LogOut } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { authService } from "@/services/authService";
import { useErrorToast } from "@/components/ui/Toast";
import { usePermissions } from "@/contexts/PermissionContext";
import { cn } from "@/lib/utils";
import type { Locale } from "@/i18n-config";
import type { Dictionary } from "@/types/dictionary";

interface UserMenuUser {
  name?: string | null;
  email?: string | null;
  avatarUrl?: string;
}

interface UserMenuProps {
  user?: UserMenuUser;
  lang: Locale;
  dict: Dictionary;
  onClose: () => void;
}

const itemClass =
  "flex items-center gap-3 px-2.5 py-[9px] rounded-lg text-[13px] font-medium text-muted-foreground hover:bg-card-hover hover:text-foreground transition-colors focus-ring w-full text-left";

export function UserMenu({ user, lang, dict, onClose }: UserMenuProps) {
  const router = useRouter();
  const showError = useErrorToast();
  const { isCompanyOwner, isSystemAdmin } = usePermissions();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const t = dict?.layout?.header as Record<string, string> | undefined;
  const s = dict?.layout?.sidebar as Record<string, string> | undefined;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await authService.logout();
      router.push(`/${lang}/login`);
    } catch (error) {
      console.error("Logout error:", error);
      showError("Çıkış yapılırken bir hata oluştu");
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="w-60 bg-card border border-bd-hi rounded-[12px] shadow-lg overflow-hidden">
      <div className="flex items-center gap-2.5 p-3.5 border-b border-border">
        <Avatar
          name={user?.name || "Kullanıcı"}
          src={user?.avatarUrl}
          size="sm"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {user?.name}
          </p>
          <p className="text-xs text-muted truncate">{user?.email}</p>
        </div>
      </div>

      <div className="p-1.5 space-y-0.5">
        <Link href={`/${lang}/profile`} className={itemClass} onClick={onClose}>
          <User className="w-[15px] h-[15px] shrink-0" aria-hidden />
          {t?.profile ?? "Profil"}
        </Link>

        {(isCompanyOwner || isSystemAdmin) && (
          <Link
            href={`/${lang}/subscription`}
            className={itemClass}
            onClick={onClose}
          >
            <CreditCard className="w-[15px] h-[15px] shrink-0" aria-hidden />
            {s?.subscription ?? "Abonelik"}
          </Link>
        )}

        <div className="my-1 border-t border-border" />

        <button
          type="button"
          disabled={isLoggingOut}
          onClick={handleLogout}
          className={cn(
            itemClass,
            "hover:bg-error-glow-12 hover:text-error-light",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          <LogOut className="w-[15px] h-[15px] shrink-0" aria-hidden />
          {isLoggingOut
            ? (t?.loggingOut ?? "Çıkış yapılıyor…")
            : (t?.logout ?? "Çıkış Yap")}
        </button>
      </div>
    </div>
  );
}
